<template>
  <div
    class="report-control"
    :class="[
      { minimized: gnbMinimized < 0, expanded: gnbMinimized > 1 },
      reportView,
      { 'is-ga': isGa }
    ]"
  >
    <div class="control-top">
      <SiteListSelector
        v-model="isShowDomainList"
        :domain-list="domains"
        :report-view="reportView"
        @set-domain="setDomain"
      >
        <template #siteLink>
          <a
            class="link-to-site"
            :href="`http://${journeyOptions.domain}`"
            target="_blank"
          >
            <i class="icon icon--ba-link"></i>
          </a>
          <div
            class="site-area"
            id="domainListDropdown"
            :class="{ active: isShowDomainList }"
          >
            <div class="url" @click="toggleDomainList">
              <p class="site_url">
                <template v-if="journeyOptions.subSid">
                  <span class="url_string">{{ journeyOptions.subDomain }}</span>
                </template>
                <template v-else>
                  <span class="url_string"
                    ><span class="matchType">[{{ domain.matchType }}] </span>
                    *.{{ journeyOptions.domain | domain }}</span
                  >
                </template>
              </p>
              <i class="icon icon--url-btn"></i>
            </div>
          </div>
          <div
            v-if="isSitemapView && journeyOptions.firstPath"
            class="selected-start-path"
          >
            {{ selectedStartPathText }}
          </div>
        </template>
      </SiteListSelector>
      <div
        class="temp-ai-data-button data"
        :class="{ minimized: gnbMinimized < 0 }"
        v-if="isAdmin && reportView !== 'mine' && !isSitemapView && !isDemo"
      >
        <ba-loading v-if="aiReportProcessing" />
        <ui-button @click="onCreateRawDataOnly" :loading="aiReportProcessing">
          Get Raw Data
        </ui-button>
      </div>

      <div v-if="!isSitemapView" class="date-control">
        <div
          v-if="reportView !== 'mine'"
          class="now-date"
          @click.stop="toggleRangePicker"
        >
          <div class="date-picker">
            <div class="periods-label">
              {{ dateRangeStr }}
              <i class="icon icon--calendar-green"></i>
            </div>
            <transition name="calendar">
              <date-range-picker
                v-if="showDateRangePicker"
                :dateRange="dateRange"
                :maxDays="184"
                :minDate="calendarMinDate"
                :maxDate="calendarMaxDate"
                :minSelectableDate="calendarMinSelectableDate"
                :timezone="timezone"
                :timezoneName="timezoneName"
                :showPaid="true"
                @onRangeCancel="showDateRangePicker = false"
                @onRangeChange="onRangeChange"
              />
            </transition>
          </div>
        </div>
      </div>
    </div>
    <div class="docent-wrap">
      <i class="icon icon--beta" />
      <div
        class="docent"
        :class="{
          disabled:
           globalNoData || reportView === 'mine' || isSitemapView,
          active: showAiDocent
        }"
        @click="onClickAiDocent"
      >
        <p class="docent-label">AI DOCENT</p>
        <div class="docent-robot"></div>
      </div>

    </div>
    <div class="control-bottom">
      <div class="view-control">
        <button
          class="report-view dashboard"
          @click="onReportViewChange('dashboard')"
        >
          <i class="icon icon--report-dashboard inactive" />
          <p class="button-label">{{ $t("common.dashboard") }}</p>
        </button>
        <button
          class="report-view ranking"
          :class="{ active: reportView === 'ranking' }"
          @click="onReportViewChange('ranking')"
        >
          <i
            :class="[
              'icon icon--report-ranking',
              { inactive: reportView !== 'ranking' }
            ]"
          />
          <p class="button-label">{{ $t("common.ranking") }}</p>
        </button>
        <button
          class="report-view journey"
          :class="{ active: reportView === 'journey' }"
          @click="onReportViewChange('journey')"
        >
          <i
            :class="[
              'icon icon--report-journey',
              { inactive: reportView !== 'journey' }
            ]"
          />
          <p class="button-label">{{ $t("common.journey") }}</p>
        </button>
        <button
          class="report-view trend"
          :class="{ active: reportView === 'trend' }"
          @click="onReportViewChange('trend')"
        >
          <i
            :class="[
              'icon icon--report-trend',
              { inactive: reportView !== 'trend' }
            ]"
          />
          <p class="button-label">{{ $t("common.trend") }}</p>
        </button>
        <button
          v-if="isAdmin"
          class="report-view sitemap"
          :class="{ active: isSitemapView }"
          @click="onReportViewChange('sitemap')"
        >
          <i
            :class="[
              'icon icon--report-sitemap',
              { inactive: !isSitemapView }
            ]"
          />
          <p class="button-label">{{ $t("common.sitemap") }}</p>
        </button>
        <button class="report-view geo disabled">
          <i class="icon icon--coming-soon-label" />
          <i :class="`icon icon--report-geo inactive`" />
          <p class="button-label">{{ $t("common.geo") }}</p>
        </button>
        <button
          class="report-view mine"
          :class="[
            { active: reportView === 'mine' },
            { disabled: !mineCount || domain.shared === 'shared' }
          ]"
          @click="onReportViewChange('mine')"
        >
          <i
            :class="[
              'icon icon--report-mine',
              { inactive: reportView !== 'mine' }
            ]"
          />
          <p class="button-label">{{ $t("common.mine") }}</p>
        </button>
        <div class="report-view empty-start"></div>
        <div class="report-view empty-end"></div>
      </div>
      <div class="segment-wrap" v-if="reportView !== 'mine' && !isSitemapView">
        <div class="campaign">
          <div
            class="campaign-target"
            @click.stop="onSettingUtm(false)"
            :class="{ disabled: noData && !utmEnabled }"
          >
            <div class="title">
              {{ $t("journey.targeting.title") }}
            </div>
            <div class="switch">
              <span
                @mouseenter="
                  utmToggleColor =
                    noData && !(utmEnabled || surveyEnabled)
                      ? '#d7d7d7'
                      : '#bbbbbb'
                "
                @mouseleave="utmToggleColor = '#d7d7d7'"
              >
                <ba-switch
                  :value="utmEnabled || surveyEnabled"
                  :default="false"
                  :onColor="'#3eaa08'"
                  :offColor="utmToggleColor"
                  :onLabel="''"
                  :offLabel="''"
                  :width="30"
                  :disabled="noData && !(utmEnabled || surveyEnabled)"
                  :size="'xsmall'"
                />
              </span>
            </div>
          </div>

          <div class="campaign-setting">
            <i
              class="icon icon--setting-size16"
              :class="{ disabled: !(utmEnabled || surveyEnabled) }"
              @click.prevent="onSettingUtm(true)"
            ></i>
          </div>
        </div>

        <div class="device-control" :class="{ all: deviceAll }">
          <ba-button-select
            :items="deviceOptions"
            :on="viewOptions.device"
            :all="deviceAll"
            :disabled="noData"
            @change="device => onDeviceChange(device, true)"
          />
          <button
            class="select-all-btn"
            @click="onDeviceChange(null, true)"
            :class="{ disabled: noData }"
          >
            <i v-if="deviceAll" class="icon icon-chain-white"></i>
            <i v-else class="icon icon-chain-red"></i>
          </button>
        </div>

        <referrer-control
          :type="referrerType"
          :refer="referrer"
          :referrers="referrers"
          :ai-referrer-providers="aiReferrerProviders"
          :selected-ai-referer-ids="selectedAiRefererIds"
          :disabled="noData || deviceSessionCount < 1"
          :refer-disabled="isReferDisabled"
          :refer-pattern-info="isReferPatternInfo"
          :refer-pattern-updated="isReferPatternUpdated"
          @type-change="onReferrerTypeChange"
          @refer-change="onReferrerChange"
          @ai-apply="applyAiReferers"
          @refer-info-over="onReferInfoOver"
          @refer-info-out="onReferInfoOut"
        />

        <div class="source-control">
          <ui-checkbox
            :value="viewOptions.new"
            :disabled="noData || deviceSessionCount < 1"
            color="green"
            @input="onSourceChange('new')"
          >
            {{ $t("common.new") }}
          </ui-checkbox>
          <ui-checkbox
            :value="viewOptions.revisit"
            :disabled="noData || deviceSessionCount < 1"
            color="green"
            @input="onSourceChange('revisit')"
          >
            {{ $t("common.return") }}
          </ui-checkbox>
        </div>
      </div>
      <mine-control v-if="reportView === 'mine'" />
      <sitemap-control v-if="isSitemapView" />
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { JOURNEY_TARGETING_VIEWABLE_START_DATE } from '@/store'
import { format, parse, addDays, max, min } from 'date-fns'
import { getDialog } from '@/libs/vue-utils'
import { getAiReferer, getBasicInfo, getTopRefer } from '@/api/journey'
import { EventBus } from '@/libs/event-bus'
import { getPercentage, utcToLocalTimezone, homeUrl } from '@/libs/utils'

import BaSwitch from '@/components/ui/ba/Switch'
import BaButtonSelect from '@/components/ui/ba/ButtonSelect'
import UiCheckbox from '@/components/ui/UiCheckbox'
import DateRangePicker from '@/components/ui/ba/DateRangePicker'
import {
  BIG_SESSION_DEFAULT_RANGE_TARGET,
  DEFAULT_TIMEZONE,
  DEFAULT_TIMEZONE_NAME,
  REFER_PATTERN_DATE
} from '@/libs/constants'
import UiButton from '@/components/ui/UiButton.vue'
import {
  createAIReportData,
  getAIReportData,
  getAIReportPlan,
  getMineCount,
  getRemainReportCredit
} from '@/api/ai'
import { create } from 'vue-modal-dialogs'
import AppDialog from '@/components/ui/AppDialog.vue'

import SiteListSelector from '@/components/report/SiteListSelector.vue'
import ReferrerControl from '@/components/report/ReferrerControl.vue'
import MineControl from '@/components/report/MineControl.vue'
import SitemapControl from '@/components/report/SitemapControl.vue'
import BaLoading from '@/components/ui/ba/Loading.vue'
import { getI18n } from '@/libs/i18n'

const openDialog = getDialog()
const aiReportOpenDialog = create(AppDialog, 'dialogType')

export default {
  name: 'JourneyControl',
  components: {
    BaLoading,
    UiButton,
    BaSwitch,
    BaButtonSelect,
    UiCheckbox,
    DateRangePicker,
    SiteListSelector,
    ReferrerControl,
    MineControl,
    SitemapControl
  },
  props: ['domains'],
  methods: {
    ...mapMutations([
      'setJourneyPeriod',
      'setJourneyOptions',
      'setJourneyExcelData',
      'setBaGnbMinimized',
      'setJourneyTargetingOptions',
      'setBasicInfo',
      'setDeviceList',
      'setViewOptions',
      'setShowAiDocent',
      'setJourneyAiReferrerProviders'
    ]),
    getAiReportCreditData () {
      getRemainReportCredit().then(res => {
        this.aiReportCredit = res
      })
    },
    onCreateRawDataOnly () {
      const reportView = this.reportView
      let title = this.$t(`aiReport.segments.title.${reportView}`)
      const data = {
        parentSid: this.parentSid,
        title,
        type: reportView,
        journeyOptions: {
          ...this.journeyOptions
        },
        journeyPeriod: {
          ...this.journeyPeriod
        },
        isOnlyData: true,
        isShow: false,
        lang: getI18n().locale
      }

      if (this.journeyTargetingOptions) {
        data.journeyOptions = {
          ...this.journeyOptions,
          ...this.journeyTargetingOptions,
          direction: this.journeyTargetingOptions.direction || 'backward',
          targetNodeId: this.journeyOptions.isCustomNode
            ? this.journeyTargetingOptions.targetNodeKey
            : this.journeyTargetingOptions.gUrlId,
          isCustomNode: this.journeyOptions.isCustomNode,
          targetNodeKey:
            this.journeyOptions.isCustomNode ||
            this.journeyOptions.matchType === 'exact'
              ? this.journeyTargetingOptions.targetNodeKey
              : null,
          startFromHead: this.targetStartFromHead
        }

        if (data.journeyOptions.rank) {
          delete data.journeyOptions.rank
        }
        if (data.journeyOptions.activatedId) {
          delete data.journeyOptions.activatedId
        }

        data.type = 'target'
      }

      const removeFieldList = [
        'referrerItems',
        'isContainPaid',
        'listSize'
      ]
      for (let i = 0; i < removeFieldList.length; i++) {
        const removeField = removeFieldList[i]
        if (data.journeyOptions[removeField]) {
          delete data.journeyOptions[removeField]
        }
      }

      createAIReportData(data, true)
        .then(res => {
          this.aiReportProcessing = true
          const aiReportId = res._id

          clearInterval(this.createAIReportChecker)
          this.createAIReportChecker = setInterval(() => {
            const data = this.aiReportProcessing
            if (data) {
              getAIReportData(aiReportId, true)
                .then(result => {
                  if (result.dataS3Path) {
                    const data = result.dataS3Path
                    const downloadUrl = `https://s3.ap-northeast-2.amazonaws.com/${data.replace(
                      'ai-report:',
                      'ai-report/'
                    )}`

                    const link = document.createElement('a')
                    link.setAttribute('target', '_blank')
                    // link.href = window.URL.createObjectURL(result.rawData) // downloadUrl
                    link.href = URL.createObjectURL(
                      new Blob([JSON.stringify(result.rawData)], {
                        type: 'application/json'
                      })
                    )
                    link.download = downloadUrl.split('/')[downloadUrl.split('/').length - 1]
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)

                    this.aiReportProcessing = false
                    clearInterval(this.createAIReportChecker)
                  }
                })
                .catch(() => {
                  // this.$set(this.captureProcessing, data, false)
                  this.aiReportProcessing = false
                })
            }
          }, 1000)
        })
        .catch(err => {
          if (err.message === 'sysError') {
            aiReportOpenDialog('report.reportRequest.sysError')
          }
        })
    },
    onReferCheck () {
      if (
        this.journeyPeriod.end >= this.referPatternDate &&
        this.journeyPeriod.start < this.referPatternDate
      ) {
        this.isReferDisabled = true
      } else {
        this.isReferDisabled = false
      }
    },
    onReferInfoOver () {
      const today = format(
        utcToLocalTimezone(new Date(), this.domain.timezoneName),
        'YYYY-MM-DD'
      )
      if (today < this.referPatternDate) {
        this.isReferPatternInfo = true
      } else {
        if (this.isReferDisabled) {
          this.isReferPatternUpdated = true
        }
      }
    },
    onReferInfoOut (disabled) {
      if (this.isReferPatternInfo) {
        this.isReferPatternInfo = false
      } else {
        this.isReferPatternUpdated = false
      }
    },

    toDashboard () {
      this.$router.push({ name: 'dashboard' })
    },

    setDomain ({ sid, domain, domainData, isSubDomain, firstPath }) {
      this.isShowDomainList = false
      this.domain = domainData
      this.selectedDomain = domainData

      const reportView = this.reportView || 'ranking'
      const to = {
        name: reportView,
        query: {
          sid
        }
      }

      if (isSubDomain) {
        to.query.sid = domainData.orgParentSid
        to.query.subSid = domainData.orgSid
        to.query.path = firstPath

        this.setJourneyOptions({
          ...this.journeyOptions,
          subDomain: domainData.domain,
          firstPath: firstPath
        })
      }

      this.init = false

      let routeData = this.$router.resolve(to)
      location.href = routeData.href
    },

    toggleRangePicker () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeReferrerList')
      EventBus.$emit('closeReportPageList')
      EventBus.$emit('closeJourneyPageList')
      this.showDateRangePicker = !this.showDateRangePicker
    },

    toggleDomainList () {
      this.isShowDomainList = !this.isShowDomainList
      this.showDateRangePicker = false
    },

    startDropdownTimer: function () {
      this.dropdownTimer = setInterval(() => {
        if (this.dropdownTimerTime < 3) {
          this.isShowDomainList = false
        }
        this.dropdownTimerTime--
      }, 1000)
    },

    resetDropdownTimer: function () {
      this.domains.forEach(item => {
        this.$set(item, 'openSubDomains', false)
      })
      this.dropdownTimerTime = 3
      clearInterval(this.dropdownTimer)
      this.dropdownTimer = null

      this.selectedDomain = this.domain
    },

    async onReportViewChange (item) {
      const exitConfirmed = await this.exitTracking()
      if (!exitConfirmed) return

      if (
        item === 'dashboard' &&
        this.reportView !== 'mine' &&
        !this.journeyOptions.subSid &&
        !this.journeyOptions.firstPath
      ) {
        window._journeyOptionsFromParent = {
          device: this.journeyOptions.device,
          exist: this.journeyOptions.exist,
          refer: this.journeyOptions.refer,
          aiRefererIds: this.journeyOptions.aiRefererIds,
          survey: this.journeyOptions.survey,
          utm: this.journeyOptions.utm
        }
        window._journeyPeriodFromParent = {
          start: this.journeyPeriod.start,
          end: this.journeyPeriod.end
        }
        window._journeyNoDataFromParent = this.noData
      }

      this.isDisabled = false
      this.isTracking = false
      this.setShowAiDocent(false)
      EventBus.$emit('reportViewChange', item, this.reportView)
      EventBus.$emit('group-state', false)
    },

    async onDeviceChange (item, direct = false) {
      if (direct) {
        const exitConfirmed = await this.exitTracking()
        if (!exitConfirmed) return
      }

      this.loading = true

      if (!item) {
        this.deviceAll = true
        this.deviceOptions = this.deviceOptions.map(option => {
          return { ...option, hideLabel: true }
        })
      } else if (this.deviceAll) {
        this.deviceAll = false
        this.deviceOptions = this.deviceOptions.map(option => {
          return { ...option, hideLabel: false }
        })
      }

      this.$set(this.viewOptions, 'device', item)
      if (!this.init) {
        const parentExist = window._journeyOptionsFromParent
          ? window._journeyOptionsFromParent.exist
          : null
        this.$set(this.viewOptions, 'new', parentExist !== true)
        this.$set(this.viewOptions, 'revisit', parentExist !== false)
        this.referrer = window._journeyOptionsFromParent
          ? window._journeyOptionsFromParent.refer
          : null
      } else {
        this.$set(this.viewOptions, 'new', true)
        this.$set(this.viewOptions, 'revisit', true)
        this.referrer = null
      }
      this.$emit('onLoading', true)
      setTimeout(() => {
        EventBus.$emit(
          'journeyOptionChange',
          'device',
          item,
          false,
          !this.init
        )
        this.setReferData()
        this.init = true
        window._journeyOptionsFromParent = null
        window._journeyPeriodFromParent = null
      }, 300)
    },

    onSetBasicInfo (item) {
      this.loading = true
      this.$set(this.viewOptions, 'device', item.device)
      this.$set(this.viewOptions, 'new', item.exist !== true)
      this.$set(this.viewOptions, 'revisit', item.exist !== false)
      const hasAiReferer = item.aiRefererIds && item.aiRefererIds.length
      this.referrer = hasAiReferer ? null : item.refer
      this.referrerType = hasAiReferer
        ? 'ai'
        : 'general'
      this.$emit('onLoading', true)
      this.loadData()
      this.init = true
    },

    async onSourceChange (item) {
      const exitConfirmed = await this.exitTracking()
      if (!exitConfirmed) return

      this.$set(this.viewOptions, item, !this.viewOptions[item])
      if (!this.viewOptions.new && !this.viewOptions.revisit) {
        this.$set(this.viewOptions, item === 'new' ? 'revisit' : 'new', true)
      }
      let sourceOption = null
      if (!this.viewOptions.new) {
        sourceOption = true
      } else if (!this.viewOptions.revisit) {
        sourceOption = false
      }
      this.setViewOptions(sourceOption)
      EventBus.$emit('journeyOptionChange', 'exist', sourceOption)
    },

    async onReferrerChange (item) {
      const exitConfirmed = await this.exitTracking()
      if (!exitConfirmed) return

      this.$set(this.viewOptions, 'new', true)
      this.$set(this.viewOptions, 'revisit', true)
      this.$emit('onLoading', true)
      this.referrer = item
      setTimeout(() => {
        EventBus.$emit('journeyOptionChange', 'refer', item)
      }, 300)
    },

    async onReferrerTypeChange (type) {
      if (this.referrerType === type) return

      const hasAiReferer = this.selectedAiRefererIds.size > 0
      const shouldResetRefer = type === 'ai' && this.referrer !== null
      const shouldResetAiReferer = type === 'general' && hasAiReferer

      if (shouldResetRefer || shouldResetAiReferer) {
        const exitConfirmed = await this.exitTracking()
        if (!exitConfirmed) return
      }

      this.referrerType = type

      if (shouldResetRefer) {
        this.referrer = null
        this.$set(this.viewOptions, 'new', true)
        this.$set(this.viewOptions, 'revisit', true)
        this.$emit('onLoading', true)
        setTimeout(() => {
          EventBus.$emit('journeyOptionChange', 'refer', null)
        }, 300)
      }

      if (shouldResetAiReferer) {
        this.$set(this.viewOptions, 'new', true)
        this.$set(this.viewOptions, 'revisit', true)
        this.$emit('onLoading', true)
        setTimeout(() => {
          EventBus.$emit('journeyOptionChange', 'aiRefererIds', null)
        }, 300)
      }
    },

    async setAiRefererIds (next) {
      const exitConfirmed = await this.exitTracking()
      if (!exitConfirmed) return

      this.referrer = null
      this.$set(this.viewOptions, 'new', true)
      this.$set(this.viewOptions, 'revisit', true)
      this.$emit('onLoading', true)
      setTimeout(() => {
        EventBus.$emit(
          'journeyOptionChange',
          'aiRefererIds',
          next.length ? next : null
        )
      }, 300)
    },

    applyAiReferers (aiRefererIds) {
      this.setAiRefererIds(aiRefererIds)
    },
    renderBasicInfoData (result) {
      if (!this.journeyOptions.utm && !this.journeyOptions.survey) {
        EventBus.$emit('sessionCount', this.sessionCount)
        EventBus.$emit('sessionTotalCount', this.sessionTotalCount)
      }

      const excelData = {
        totalSession: this.sessionTotalCount,
        sessionCount: { ...this.sessionCount },
        refer: {}
      }

      this.referrer = null
      for (const [idx, device] of [
        'desktop',
        'tablet',
        'phone',
        'all'
      ].entries()) {
        if (device !== 'all' && !this.isSubdomainResample) {
          this.deviceOptions[idx].label =
            getPercentage(
              this.sessionCount[device],
              this.sessionTotalCount
            ).toFixed(1) + '%'
        }

        this.referrerItems[device] = [{ value: null, text: null }]
        excelData.refer[device] = {}
        excelData.refer[device][null] = {
          text: 'All',
          value: 0
        }
      }
      this.setJourneyOptions({
        ...this.journeyOptions,
        referrerItems: this.referrerItems
      })
      this.setDeviceList(this.deviceOptions)
      this.setJourneyExcelData(excelData)

      if (!this.init) {
        const currentSid = this.journeyOptions.sid
        if (BIG_SESSION_DEFAULT_RANGE_TARGET.includes(currentSid)) {
          this.defaultDateRange = -1
        }
        const now = new Date()
        let periodMin = utcToLocalTimezone(
          max(
            parse(this.currentDomain.installDate || '1970-01-01'),
            addDays(now, -397)
          ),
          this.domain.timezoneName
        )
        let periodMax = utcToLocalTimezone(
          max(parse(result.end || addDays(now, -397)), now),
          this.domain.timezoneName
        )
        let periodStart, periodEnd

        if (window._journeyPeriodFromParent) {
          periodStart = parse(this.journeyPeriod.start)
          periodEnd = parse(this.journeyPeriod.end)
        } else if (this.journeyPluck) {
          const pluckStart =
            this.journeyPluck.start || this.journeyPluck.startDateStr
          const pluckEnd =
            this.journeyPluck.end || this.journeyPluck.endDateStr
          periodStart = parse(pluckStart)
          periodEnd = parse(pluckEnd)
        } else if (this.aiReportData) {
          periodStart = parse(this.aiReportData.startDateStr)
          periodEnd = parse(this.aiReportData.endDateStr)
        } else {
          periodStart = max(
            periodMin,
            addDays(
              utcToLocalTimezone(now, this.domain.timezoneName),
              this.defaultDateRange
            )
          )
          periodEnd = min(
            periodMax,
            utcToLocalTimezone(now, this.domain.timezoneName)
          )
        }

        this.suppressPeriodChange = true
        this.setJourneyPeriod({
          ...this.journeyPeriod,
          start: format(periodStart, 'YYYY-MM-DD'),
          end: format(periodEnd, 'YYYY-MM-DD'),
          min: format(periodMin, 'YYYY-MM-DD'),
          max: format(periodMax, 'YYYY-MM-DD')
        })

        this.$emit('onLoading', 'init')
      }

      // trend에서 aiReportData가 있으면 onSetBasicInfo 호출하고 리턴
      if (this.reportView === 'trend' && this.aiReportData) {
        this.onSetBasicInfo(this.aiReportData)
        return
      }

      // device 선택 로직
      if (this.journeyPluck) {
        this.onSetBasicInfo(this.journeyPluck)
      } else if (this.viewOptions.device === null) {
        const initialDevice = window._journeyOptionsFromParent
          ? window._journeyOptionsFromParent.device
          : null
        this.onDeviceChange(initialDevice)
      }

      if (window._journeyOptionsFromParent) {
        if (window._journeyOptionsFromParent.isCustomNode) {
          this.setJourneyOptions({
            ...this.journeyOptions,
            isCustomNode: window._journeyOptionsFromParent.isCustomNode
          })
        }
        if (window._journeyOptionsFromParent.refer) {
          this.referrer = window._journeyOptionsFromParent.refer
          this.setJourneyOptions({
            ...this.journeyOptions,
            refer: this.referrer
          })
        }
        if (window._journeyOptionsFromParent.aiRefererIds) {
          this.referrerType = 'ai'
          this.referrer = null
          this.setJourneyOptions({
            ...this.journeyOptions,
            refer: null,
            aiRefererIds: window._journeyOptionsFromParent.aiRefererIds
          })
        }
        if (window._journeyOptionsFromParent.exist !== null) {
          this.setJourneyOptions({
            ...this.journeyOptions,
            exist: window._journeyOptionsFromParent.exist
          })
        }
      }
    },
    setReferData () {
      if (this.journeyOptions.aiRefererIds && this.journeyOptions.aiRefererIds.length) {
        this.referrerType = 'ai'
        this.referrer = null
      }
      const params = {
        ...this.infoOption,
        device: this.viewOptions.device
      }
      this.setAiRefererData()
      getTopRefer(params).then(res => {
        const excelData = {
          refer: {
            [this.viewOptions.device || 'all']: {
              null: {
                text: 'All',
                value: 0
              }
            }
          }
        }

        for (let item of res.total) {
          const [refer, session] = Object.entries(item)[0]
          let referText = refer
          if (refer.substr(0, 4) === 'http') {
            const referObj = document.createElement('a')
            referObj.href = refer
            referText = refer.substr(referObj.protocol.length + 2)
          }

          const totalSession = this.viewOptions.device
            ? this.sessionCount[this.viewOptions.device]
            : this.sessionTotalCount

          const referData = {
            value: refer,
            text: referText,
            additionalText: `(${getPercentage(session, totalSession).toFixed(
              1
            )}%)`
          }

          excelData.refer[this.viewOptions.device || 'all'][refer] = {
            text: referText,
            value: session
          }

          this.referrerItems[this.viewOptions.device || 'all'].push(referData)
        }
        this.setJourneyExcelData(excelData)
      })
    },
    setAiRefererData () {
      const params = {
        ...this.infoOption,
        device: this.viewOptions.device
      }
      getAiReferer(params).then(res => {
        const totalSession = this.viewOptions.device
          ? this.sessionCount[this.viewOptions.device]
          : this.sessionTotalCount

        this.aiReferrerProviders = (res.total || []).map(group => ({
          ...group,
          additionalText: `${getPercentage(group.count, totalSession).toFixed(1)}%`,
          values: group.values.map(value => ({
            ...value,
            additionalText: `${getPercentage(value.count, totalSession).toFixed(1)}%`
          }))
        }))
        this.setJourneyAiReferrerProviders(this.aiReferrerProviders)
      }).catch(() => {
        this.aiReferrerProviders = []
        this.setJourneyAiReferrerProviders([])
      })
    },
    loadBasicInfoData () {
      if (this.isSitemapView) {
        this.loadData()
        return
      }

      if (window._journeyOptionsFromParent) {
        this.setJourneyOptions({
          ...this.journeyOptions,
          utm: window._journeyOptionsFromParent.utm || null,
          survey: window._journeyOptionsFromParent.survey || null
        })
      }

      getBasicInfo(this.infoOption, true)
        .then(result => {
          this.basicInfo = result
          this.sessionTotalCount = result.session || 0
          this.sessionCount = {
            desktop: result.total.desktop || 0,
            tablet: result.total.tablet || 0,
            phone: result.total.phone || 0
          }
          this.noData = this.sessionTotalCount < 1

          this.settingJourneyBasicInfo(result)
          this.renderBasicInfoData(result)
        })
        .catch(() => {
          EventBus.$emit('onError')
          EventBus.$emit('onLoading', false)
        })
    },
    loadData () {
      if (this.reportView === 'mine') {
        return false
      }
      if (this.isSitemapView) {
        EventBus.$emitSticky('loadPaneData')
        return
      }
      if (this.sessionTotalCount > 0) {
        EventBus.$emitSticky('loadPaneData')
      } else {
        EventBus.$emitSticky('loadPaneNoData', true)
      }
    },

    async onRangeChange (data) {
      const exitConfirmed = await this.exitTracking()
      if (!exitConfirmed) return

      const range = data.range
      const rangeStart = format(range.startDate, 'YYYY-MM-DD')
      const rangeEnd = format(range.endDate, 'YYYY-MM-DD')

      if (
        this.journeyPeriod.start !== rangeStart ||
        this.journeyPeriod.end !== rangeEnd ||
        this.isTracking
      ) {
        if (this.utmEnabled || this.surveyEnabled) {
          openDialog({
            dialogType: 'journey.changePeriodResetUtm',
            ok: this.$t('common.yes'),
            cancel: this.$t('common.no')
          }).then(result => {
            if (result) {
              this.setRange(rangeStart, rangeEnd)
            }
          })
        } else {
          this.setRange(rangeStart, rangeEnd)
        }
      }
      this.showDateRangePicker = false
    },

    setRange (rangeStart, rangeEnd) {
      this.loading = true
      this.$emit('onLoading', true)
      setTimeout(() => {
        EventBus.$emit('journeyPeriodChange', {
          ...this.journeyPeriod,
          start: rangeStart,
          end: rangeEnd
        })
      }, 300)
    },

    onPeriodChange () {
      this.onReferCheck()
      this.referrerType = 'general'

      if (this.reportView === 'mine') {
        return
      }

      if (this.init) {
        this.viewOptions.device = null
        this.loadBasicInfoData()
      }
    },

    async onSettingUtm (force) {
      const exitConfirmed = await this.exitTracking()
      if (!exitConfirmed) return

      if (!(this.noData && !this.utmEnabled && !this.surveyEnabled)) {
        EventBus.$emit('settingUtm', force, true)
      }
    },

    settingJourneyBasicInfo (result) {
      this.setBasicInfo(result)
    },
    // onSaveLocalStorageForCapture () {
    //   const data = {
    //     type: this.reportView,
    //     journeyOptions: this.journeyOptions,
    //     journeyPeriod: this.journeyPeriod
    //   }
    //   console.log(data)
    //   delete data.journeyOptions.referrerItems
    //
    //   localStorage.setItem('journeymap_capture', JSON.stringify(data))
    // },
    getParams () {
      let title = ''
      const reportView = this.reportView
      title = this.selectedReportInfo ? this.selectedReportInfo.title : ''
      const data = {
        parentSid: this.parentSid,
        title,
        type: reportView,
        journeyOptions: {
          ...this.journeyOptions
        },
        journeyPeriod: {
          ...this.journeyPeriod
        }
      }

      if (this.selectedReportInfo && this.selectedReportInfo.reportData) {
        data.journeyOptions = {
          ...this.journeyOptions,
          ...this.selectedReportInfo.reportData.options
        }
        data.journeyPeriod.start = this.selectedReportInfo.reportData.options.startDateStr
        data.journeyPeriod.end = this.selectedReportInfo.reportData.options.endDateStr

        if (this.journeyTargetingOptions) {
          data.journeyOptions = {
            ...data.journeyOptions,
            ...this.journeyTargetingOptions,
            direction: this.journeyTargetingOptions.direction || 'backward',
            targetNodeId: this.journeyOptions.isCustomNode
              ? this.journeyTargetingOptions.targetNodeKey
              : this.journeyTargetingOptions.gUrlId,
            isCustomNode: this.journeyOptions.isCustomNode,
            targetNodeKey:
              this.journeyOptions.isCustomNode ||
              this.journeyOptions.matchType === 'exact'
                ? this.journeyTargetingOptions.targetNodeKey
                : null,
            startFromHead: this.targetStartFromHead
          }

          if (data.journeyOptions.rank) {
            delete data.journeyOptions.rank
          }
          if (data.journeyOptions.activatedId) {
            delete data.journeyOptions.activatedId
          }

          data.type = 'target'
        }
      }

      const removeFieldList = [
        'referrerItems',
        'isContainPaid',
        'listSize'
      ]
      for (let i = 0; i < removeFieldList.length; i++) {
        const removeField = removeFieldList[i]
        if (data.journeyOptions[removeField]) {
          delete data.journeyOptions[removeField]
        }
      }
      return data
    },
    onCreateRawDataForAIReport () {
      const data = this.getParams()
      data.reportPaymentId = this.reportPaymentId
      data.reportPaymentType = this.reportPaymentType
      data.lang = getI18n().locale
      createAIReportData(data, true)
        .then(res => {
          this.aiReportProcessing = true
          // const aiReportId = res._id

          aiReportOpenDialog('report.reportRequest.success')
          this.aiReportProcessing = false
        })
        .catch(err => {
          if (err.message === 'sysError') {
            aiReportOpenDialog('report.reportRequest.sysError')
          }
        })
    },
    onLocalStorageCheck () {
      const journeymapData = localStorage.getItem('journeymap_capture')
      if (journeymapData) {
        const data = JSON.parse(journeymapData)
        const journeyOptions = data.journeyOptions
        const journeyPeriod = data.journeyPeriod
        const parentSid = data.parentSid

        return { journeyOptions, journeyPeriod, parentSid }
      } else {
        return null
      }
    },
    getMineInfo () {
      const requestData = {
        parentSid: this.parentSid,
        matchType: this.journeyOptions.matchType,
        lang: this.$i18n.locale
      }

      getMineCount(requestData, true).then(res => {
        this.mineCount = res
      })
    },
    async exitTracking () {
      if (!this.isTracking) return true

      const confirmed = await openDialog({
        dialogType: 'tracking.stopTrackingWithDesc',
        optionContent: { style: 'height: 14px;' }
      })
      return confirmed
    },
    onClickAiDocent () {
      this.setShowAiDocent(!this.showAiDocent)
    }
  },
  async created () {
    EventBus.$on('paneDisabled', value => {
      this.isDisabled = value
    })

    EventBus.$on('journeyOptionChange', (key, value, preventLoad) => {
      if (!preventLoad) {
        const preventDataLoad = ['utm', 'survey', 'matchType'].includes(key)
        if (!preventDataLoad) {
          this.loadData()
        }

        setTimeout(() => {
          if (['utm', 'survey', 'matchType'].includes(key)) {
            this.$set(this.viewOptions, 'device', null)
            this.loadBasicInfoData()
          }
        }, 500)
      }
    })

    EventBus.$on('journeyPluckChange', (data = {}) => {
      setTimeout(() => {
        this.loadBasicInfoData()
      }, 300)
    })

    EventBus.$on('reportViewChange', (data = {}) => {
      if (data !== 'dashboard') {
        setTimeout(() => {
          this.onPeriodChange()
        }, 300)
      }
    })

    EventBus.$on('tracking-state', value => {
      this.isTracking = value
    })

    this.domains.forEach(item => {
      if (item.orgSid === this.journeyOptions.sid) {
        this.domain = item

        if (item.subDomains) {
          item.subDomains.forEach(subItem => {
            if (subItem.orgSid === this.journeyOptions.subSid) {
              this.setJourneyOptions({
                ...this.journeyOptions,
                subDomain: subItem.domain
              })
              this.selectedDomain = subItem
            }
          })
        }
        // this.selectedDomain = item
        this.timezone = item.timezone
        this.timezoneName = item.timezoneName
      }
    })
    getAIReportPlan().then(resp => {
      this.reportPlan = resp
    })
    this.viewOptions.report = this.reportView

    this.onReferCheck()

    if (this.isSitemapView) {
      this.loadData()
    } else if (this.reportView === 'mine') {
      this.$emit('onLoading', 'init')
    } else {
      setTimeout(() => {
        this.loadBasicInfoData()
      }, 1000)
    }

    this.getMineInfo()
  },

  destroyed () {
    this.init = false
    EventBus.$off('journeyOptionChange')
    EventBus.$off('journeyPluckChange')
    EventBus.$off('reportViewChange')
    EventBus.$off('tracking-state')
    // EventBus.$off('loadPaneNoData')
  },

  watch: {
    journeyPeriod () {
      if (this.suppressPeriodChange) {
        this.suppressPeriodChange = false
        return
      }
      this.onPeriodChange()
    },
    'journeyOptions.exist' (value) {
      this.$set(this.viewOptions, 'new', value === null || value === false)
      this.$set(this.viewOptions, 'revisit', value === null || value === true)
    },
    'journeyOptions.utm' (value) {
      setTimeout(() => {
        this.utmOptions = value
      }, 700)
    },
    'journeyOptions.survey' (value) {
      setTimeout(() => {
        this.surveyOptions = value
      }, 700)
    },
    reportView (value) {
      this.$set(this.viewOptions, 'report', value)
    },
    isShowDomainList (value) {
      let bodyListener = e => {
        let outside = true

        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        const path =
          e.path ||
          (e.composedPath && e.composedPath()) ||
          composedPath(e.target)
        if (path) {
          for (const el of path) {
            if (el.id && el.id === 'domainListDropdown') {
              outside = false
            }
          }
        }
        if (outside) {
          this.isShowDomainList = false
        }
      }

      if (value) {
        document.body.addEventListener('click', bodyListener, false)
      } else {
        document.body.removeEventListener('click', bodyListener)
      }
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'journeyPeriod',
      'journeyOptions',
      'journeyTargetingOptions',
      'journeyPluck',
      'journeyExcelData',
      'gnbMinimized',
      'isSampling',
      'aiReportData',
      'globalNoData',
      'isDemo',
      'targetStartFromHead',
      'showAiDocent'
    ]),
    parentSid () {
      return 's' + this.$route.query.sid
    },
    isSubdomainResample () {
      return this.journeyOptions.subSid && this.isSampling
    },
    isAvailableSubdomain () {
      return this.reportView !== 'trend'
    },
    openSubDomainsList () {
      return (
        this.selectedDomain &&
        this.selectedDomain.openSubDomains &&
        this.selectedDomain.existSubdomains
      )
    },
    cacheKey () {
      return `${this.dataOption.device}_${this.dataOption.start}_${
        this.dataOption.end
      }_${this.dataOption.sid}_
      ${this.dataOption.matchType}_${this.dataOption.exist}_${
  this.dataOption.refer
}_${this.dataOption.limit}_
      ${JSON.stringify(this.dataOption.utm)}_${JSON.stringify(
  this.dataOption.survey
)}_
      ${this.dataOption.granularity}`
    },
    reportView () {
      return this.$route.name
    },
    isSitemapView () {
      return this.reportView === 'sitemap'
    },
    selectedStartPathText () {
      const firstPath = this.journeyOptions.firstPath
      if (!firstPath) return ''
      return `/${String(firstPath).replace(/^\/+|\/+$/g, '')}/*`
    },
    deviceSessionCount () {
      if (this.sessionCount) {
        return this.viewOptions.device
          ? this.sessionCount[this.viewOptions.device]
          : this.totalSessionCount
      }
      return false
    },
    referrers () {
      const refs = this.referrerItems[this.journeyOptions.device || 'all']

      return refs.map(item => {
        return {
          ...item,
          text:
            item.value === null ? this.$t('common.all') : decodeURI(item.text)
        }
      })
    },
    selectedAiRefererIds () {
      return new Set(this.journeyOptions.aiRefererIds || [])
    },
    currentDomain () {
      for (const domain of this.domains) {
        if ([domain.orgSid, domain.sid].includes(this.journeyOptions.sid)) {
          return domain
        }
      }
      return null
    },
    dateRange () {
      return {
        startDate: parse(this.journeyPeriod.start),
        endDate: parse(this.journeyPeriod.end)
      }
    },
    dateRangeStr () {
      return `${format(this.dateRange.startDate, 'MMM DD, YYYY')} - ${format(
        this.dateRange.endDate,
        'MMM DD, YYYY'
      )}`
    },
    calendarMinSelectableDate () {
      if (
        this.$route.name === 'journey' &&
        this.journeyTargetingOptions !== null
      ) {
        return parse(JOURNEY_TARGETING_VIEWABLE_START_DATE)
      }
      return null
    },
    calendarMinDate () {
      if (this.journeyOptions.firstPath) {
        return parse('2024-09-01')
      }
      if (this.domain.shared === 'shared') {
        return parse('2024-09-01')
      }
      if (this.journeyPeriod.min) {
        return parse(this.journeyPeriod.min)
      } else {
        return null
      }
    },
    calendarMaxDate () {
      if (this.journeyPeriod.max) {
        return parse(this.journeyPeriod.max)
      } else {
        return utcToLocalTimezone(new Date(), this.timezoneName)
      }
    },
    dataOption () {
      return {
        device: this.journeyOptions.device,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        sid: this.journeyOptions.sid,
        subSid: this.journeyOptions.subSid,
        firstPathname: this.journeyOptions.firstPath,
        matchType: this.journeyOptions.matchType,
        refer: this.journeyOptions.refer,
        aiRefererIds: this.journeyOptions.aiRefererIds,
        exist: this.journeyOptions.exist,
        utm: this.journeyOptions.utm,
        survey: this.journeyOptions.survey
      }
    },
    infoOption () {
      return {
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        sid: this.journeyOptions.sid,
        subSid: this.journeyOptions.subSid,
        firstPathname: this.journeyOptions.firstPath,
        matchType: this.journeyOptions.matchType,
        utm: this.journeyOptions.utm,
        survey: this.journeyOptions.survey,
        isTrend: this.reportView === 'trend'
      }
    },
    surveyOptionCount () {
      let count = 0
      if (this.surveyOptions) {
        if (this.surveyOptions.notTarget) {
          count++
        }
        if (this.surveyOptions.notAnswered) {
          count++
        }
        if (this.surveyOptions.answerOptions) {
          for (const answer of Object.values(
            this.surveyOptions.answerOptions
          )) {
            if (answer === true) {
              count++
            } else {
              count += answer.length
            }
          }
        }
      }
      return count
    },

    utmEnabled () {
      return !!this.journeyOptions.utm
    },

    surveyEnabled () {
      return !!this.journeyOptions.survey
    },

    utmCount () {
      let counts = {}
      for (const tag of this.utmTagList) {
        if (this.utmOptions && this.utmOptions[tag]) {
          counts[tag] = this.utmOptions[tag].length
          if (counts[tag] > 999) {
            counts[tag] = '999+'
          }
        }
      }
      return counts
    },
    isGa () {
      return this.domain.orgSid === '56e7142775'
    },
    isAdmin () {
      return this.userData.userId === 'admin'
    }

  },
  data () {
    return {
      initSegmentButton: false,
      reportPaymentType: null,
      reportPaymentId: null,
      isChargingJam: false,
      remainJam: true,
      openTossView: false,
      setPayment: false,
      aiReportProcessing: false,
      isAIReportId: false,
      isDisabled: false,
      init: false,
      loading: true,
      noData: false,
      suppressPeriodChange: false,
      isShowDomainList: false,
      dropdownTimer: null,
      dropdownTimerTime: null,
      basicInfo: null,
      compareRange: {
        startDate: null,
        endDate: null
      },
      showDateRangePicker: false,
      deviceOptions: [
        {
          name: 'desktop',
          icon: 'icon--device desktop',
          iconHover: 'icon--device desktop hover',
          iconActive: 'icon--device desktop on',
          colorActive: '#EC0047',
          label: 'Desktop',
          value: 'desktop'
        },
        {
          name: 'tablet',
          icon: 'icon--device tablet',
          iconHover: 'icon--device tablet hover',
          iconActive: 'icon--device tablet on',
          colorActive: '#EC0047',
          label: 'Tablet',
          value: 'tablet'
        },
        {
          name: 'phone',
          icon: 'icon--device phone',
          iconHover: 'icon--device phone hover',
          iconActive: 'icon--device phone on',
          colorActive: '#EC0047',
          label: 'Phone',
          value: 'phone'
        }
      ],
      deviceAll: false,
      referrer: null,
      referrerType: 'general',
      aiReferrerProviders: [],
      referrerItems: {
        desktop: [{ value: null, text: null }],
        tablet: [{ value: null, text: null }],
        phone: [{ value: null, text: null }],
        all: [{ value: null, text: null }]
      },
      viewOptions: {
        report: null,
        new: true,
        revisit: true,
        device: null
      },
      sessionCount: null,
      sessionTotalCount: 0,
      utmToggleColor: '#d7d7d7',

      surveyOptions: null,
      utmOptions: null,
      utmTagList: ['source', 'medium', 'campaign', 'term', 'content'],
      domain: null,
      timezone: DEFAULT_TIMEZONE,
      timezoneName: DEFAULT_TIMEZONE_NAME,
      isReferPatternInfo: false,
      isReferPatternUpdated: false,
      referPatternDate: REFER_PATTERN_DATE,
      isReferDisabled: false,
      selectedDomain: null,
      defaultDateRange: -6,
      mineCount: 0,
      bannerContents: {
        main: '',
        sub: ''
      },
      segmentMetaList: [],
      selectedReportInfo: null,
      aiReportCredit: null,
      isTracking: false,
      homeUrl: homeUrl
    }
  }
}
</script>

<style lang="scss" scoped>
.report-control {
  display: flex;
  height: 100%;
  background: #fff;

  .control-top {
    position: relative;
    z-index: 2;
    height: 100%;
    min-width: 257px;
    padding: 50px 0 0 16px;
    box-sizing: border-box;
    transition: height 0.3s ease;
    white-space: nowrap;
  }

  .control-bottom {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
    height: 100%;
    box-sizing: border-box;
    white-space: nowrap;
    transition: transform 0.3s ease;
  }

  .site-list {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    transition: transform 0.3s ease;

    .link-to-site {
      cursor: pointer;
      .icon {
        height: 16px;
        width: 16px;
        background-size: 100% 100%;
        vertical-align: middle;
      }
    }

    .selected-start-path {
      position: absolute;
      top: 20px;
      left: 24px;
      max-width: 220px;
      font-size: 14px;
      line-height: 16px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #666666;
      font-weight: 500;
    }

    .site-area {
      position: relative;
      display: inline-block;
      vertical-align: middle;
      z-index: 3;

      .type {
        height: 28px;
        line-height: 28px;
        display: inline-block;
        width: 100px;
        margin-right: 13px;
        border-radius: 5px;
        border: 1px solid #4fbae1;
        vertical-align: middle;
        color: #4fbae1;
        text-align: center;
      }

      .url {
        display: flex;
        align-items: center;
        gap: 10px;
        max-width: 500px;
        overflow: hidden;

        .icon--url-btn {
          transition: transform 0.3s ease;
          vertical-align: top;
          margin: 0;
        }
      }

      .site_url {
        display: inline-block;
        word-wrap: normal;

        .url_string {
          text-overflow: ellipsis;
          overflow: hidden;
          max-width: 173px;
          white-space: nowrap;
          font-size: 14px;
          line-height: 16px;
          color: #666666;
          font-weight: 500;

          .matchType {
            text-transform: capitalize;
          }
        }
      }

      .url-list {
        position: absolute;
        top: 23px;
        left: 0;
        z-index: 5;
        width: 300px;
        height: 346px;
        border-radius: 12px;
        box-shadow: 0 16px 15px -4px rgba(0, 0, 0, 0.12),
          0 0 0 1px rgba(0, 0, 0, 0.16);
        background-color: #ffffff;
        transform: scaleY(0);
        transform-origin: top;
        transition: transform 0.3s ease, opacity 0.2s ease;
        opacity: 0;

        &.subdomain {
          border-bottom-right-radius: 0px;
          border-top-right-radius: 0px;
        }

        h5 {
          font-size: 13px;
          font-weight: 500;
          color: #ec0047;
          padding: 15px 20px 32px 20px;
        }

        ul {
          max-height: 278px;
          overflow-y: auto;

          &:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            border-bottom-left-radius: 12px;
            z-index: 99999;
            height: 25px;
            background-image: -webkit-gradient(
              linear,
              left top,
              left bottom,
              from(rgba(255, 255, 255, 0.3)),
              to(#fff)
            );
            background-image: linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.3),
              #fff
            );
          }

          li {
            padding: 8px 10px;
            overflow: hidden;
            text-overflow: ellipsis;

            button {
              width: 100%;
              text-overflow: ellipsis;
              height: 24px;
              border-radius: 12px;
              font-size: 14px;
              line-height: 24px;
              padding: 0 30px 0 10px;
              background-color: #ffffff;
              transition: background-color 0.2s ease;
              text-align: left;
              color: #2f2f2f;
              position: relative;
              overflow: hidden;

              .icon {
                position: absolute;
                top: 4px;
                right: 8px;
                opacity: 0;
                transition: opacity 0.2s ease;
              }

              &:hover {
                background-color: #ebebeb;

                .icon--arrow-right-medium-black {
                  opacity: 1;
                }
              }

              &.active {
                background-color: #ec0047;
                color: #ffffff;

                .icon {
                  opacity: 1;
                }

                .icon--arrow-right-medium-black {
                  opacity: 0;
                }
              }
            }
          }
        }
      }

      .subdomain-url-list {
        position: absolute;
        top: 23px;
        left: 300px;
        z-index: 5;
        width: 300px;
        height: 346px;
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
        box-shadow: 0 16px 15px -4px rgba(0, 0, 0, 0.12),
          0 0 0 1px rgba(0, 0, 0, 0.16);
        background-color: #ffffff;
        transform: scaleY(0);
        transform-origin: top;
        transition: transform 0.3s ease, opacity 0.2s ease;
        opacity: 0;

        &:after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          border-bottom-right-radius: 12px;
          z-index: 99999;
          height: 25px;
          background-image: -webkit-gradient(
            linear,
            left top,
            left bottom,
            from(rgba(255, 255, 255, 0.3)),
            to(#fff)
          );
          background-image: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0.3),
            #fff
          );
        }

        h5 {
          font-size: 13px;
          font-weight: 500;
          color: #ec0047;
          padding: 15px 20px 26px 20px;
          position: relative;

          & span {
            position: relative;
            display: inline-block;

            &.title {
              top: -6px;
            }

            &.info {
              top: -2px;
              left: 5px;

              & .icon {
                position: relative;
              }

              & .bubble {
                z-index: 100;
                position: absolute;
                bottom: 30px;
                left: 50%;
                transform: translate(-50%, 20px);
                opacity: 0;
                pointer-events: none;
                transition: transform 0.15s ease, opacity 0.15s ease;
              }

              &:hover {
                .bubble {
                  transform: translate(-50%, 0);
                  opacity: 1;
                }
              }
            }
          }
        }

        ul {
          max-height: 278px;
          overflow-y: auto;

          li {
            padding: 8px 10px;
            overflow: hidden;
            text-overflow: ellipsis;

            button {
              width: 100%;
              text-overflow: ellipsis;
              height: 24px;
              border-radius: 12px;
              font-size: 14px;
              line-height: 24px;
              padding: 0 30px 0 10px;
              background-color: #ffffff;
              transition: background-color 0.2s ease;
              text-align: left;
              color: #2f2f2f;
              position: relative;
              overflow: hidden;

              .subdomain-name {
                margin-left: 15px;
              }

              .icon {
                position: absolute;
                top: 4px;
                right: 8px;
                opacity: 0;
                transition: opacity 0.2s ease;

                &.icon--enter {
                  opacity: 1;
                  right: inherit;
                  left: 5px;
                  top: 4px;
                }
              }

              &:hover {
                background-color: #ebebeb;
              }

              &.active {
                background-color: #ec0047;
                color: #ffffff;

                .icon {
                  opacity: 1;
                }
              }
            }
          }
        }
      }

      svg {
        margin-left: 5px;
      }

      & > .url {
        cursor: pointer;
      }

      &.active {
        .url {
          .icon--url-btn {
            transform: scaleY(-1);
          }
        }

        .url-list,
        .subdomain-url-list {
          transform: scaleY(1);
          opacity: 1;
        }
      }
    }

    .window .site-area .site_url {
      position: relative;
    }

    .match-type {
      float: left;
      margin-right: 6px;
    }
  }

  .date-control {
    margin-top: 9px;

    .date-picker {
      position: relative;
      border-radius: 8px;
      vertical-align: middle;
      display: inline-block;

      .periods-label {
        cursor: pointer;
        width: 100%;
        font-weight: 600;
        font-size: 16px;
        color: #0e90a5;
        line-height: 1.25;
        transition: color 0.2s ease;
      }

      .icon--calendar-green {
        margin-top: -3px;
        vertical-align: middle;
        pointer-events: none;
        padding: 2px;
        border-radius: 3px;
        border: solid 1px transparent;
        background-size: 16px 16px;
        transition: border-color 0.2s ease;
      }

      &:hover {
        .periods-label {
          color: #035e6d;
        }

        .icon--calendar-green {
          border-color: #d9d9d9;
        }
      }

      .date_range_picker {
        top: 20px;
        left: 0px;
        margin: 0;
        transform: none;
      }
    }

    .now-date {
      .date-picker {
        border-color: #e0e0e0;

        input {
          font-weight: 500;
          color: #0e90a5;
        }
      }
    }
  }

  .view-control {
    position: relative;
    display: flex;
    padding: 6px 0 0 0px;
    transition: transform 0.3s ease;

    &:before {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 16px;
      height: 1px;
      background-color: $brandColor;
      transform: translateX(-100%);
    }
    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: -16px;
      width: 10px;
      height: 10px;
      background-image: url("../../assets/img/control/icon--tab-dot.svg");
      background-size: auto;
      background-repeat: no-repeat;
      background-position: center center;
      transform: translate(-50%, 50%);
    }

    .report-view {
      position: relative;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-width: 110px;
      height: 44px;
      padding: 0 14px 0 10px;
      transition: color 0.3s ease, background-color 0.3s ease;
      border-bottom: 1px solid $brandColor;
      box-sizing: border-box;

      .button-label {
        color: #888;
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
      }

      .icon--coming-soon-label {
        position: absolute;
        top: -1px;
        left: 50%;
        transform: translateX(-50%);
      }

      &.empty-start {
        flex: 1;
        min-width: 0;
        padding: 0;
        pointer-events: none;
      }
      &.empty-end {
        position: relative;
        width: 200px;
        border-bottom: none;
        pointer-events: none;
        &:after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 200px;
          height: 1px;
          background: linear-gradient(
            90deg,
            #ec0047 0%,
            rgba(236, 0, 71, 0) 75%
          );
        }
        @media screen and (max-width: 1360px) {
          width: 98px;
          min-width: 98px;
          &::after {
            width: 98px;
          }
        }
      }

      &:hover {
        .button-label {
          color: #444;
        }
        .icon {
          filter: brightness(0.48) contrast(1.15);
        }
      }

      &.active {
        z-index: 2;
        border-width: 1px 1px 0;
        border-style: solid;
        border-color: $brandColor;
        border-radius: 6px 6px 0 0;
        [class^="icon icon--report"] {
          transform: translateY(-2px);
        }
        .button-label {
          color: $brandColor;
          transform: translateY(-2px);
        }
        &:hover {
          .icon {
            filter: none;
          }
        }
        &:before {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 5px;
          height: 5px;
          background-image: url("../../assets/img/control/icon--tab-corner.svg");
          background-size: cover;
          transform: translateX(-100%);
        }
        &:after {
          content: "";
          position: absolute;
          bottom: 0;
          right: 0;
          width: 5px;
          height: 5px;
          background-image: url("../../assets/img/control/icon--tab-corner.svg");
          background-size: cover;
          transform: translateX(100%) scaleX(-1);
        }
      }

      &.disabled {
        pointer-events: none;
        [class^="icon icon--report"],
        .button-label {
          opacity: 0.3;
        }
      }
    }
  }

  .campaign {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 10px 0 14px;
    border-radius: 5px;
    box-sizing: border-box;
    border: solid 1px transparent;
    cursor: pointer;
    transition: border-color 0.2s ease, opacity 0.3s ease;

    &:hover {
      border-color: #d9d9d9;
    }

    &.disabled {
      opacity: 0.3;
    }
  }

  .campaign-setting {
    display: inline-block;
    vertical-align: top;
    cursor: pointer;
    transition: border-color 0.2s ease, opacity 0.3s ease;

    .icon--setting-size16 {
      vertical-align: middle;
      display: inline-block;
      cursor: pointer;
      border-radius: 11px;
      background-size: 16px 16px;
      transition: background-color 0.2s ease, width 0.3s ease,
        margin-right 0.3s ease, opacity 0.3s ease;

      &.disabled {
        opacity: 0;
        width: 0;
        margin-right: 0;
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }

  .campaign-target {
    display: flex;
    align-items: center;
    gap: 6px;
    border-radius: 5px;
    box-sizing: border-box;
    border: solid 1px transparent;
    white-space: normal;
    cursor: pointer;
    transition: border-color 0.2s ease, opacity 0.3s ease;

    &.disabled {
      .title {
        opacity: 0.3;
      }
    }

    .title {
      color: #555;
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
    }

    .switch {
      position: relative;

      .campaign-info-item {
        position: relative;
        font-size: 12px;
        font-weight: normal;
        text-align: left;
        line-height: 14px;
        padding-right: 36px;
        display: block;
        cursor: pointer;
        transition: color 0.15s ease-out;

        &:hover {
          color: #0074ff;
        }

        & + .campaign-info-item {
          margin-top: 11px;
        }

        .value {
          position: absolute;
          right: 0;
          top: 0;
          font-size: 11px;
          color: #ec0047;
          line-height: 15px;
        }
      }
    }
  }

  .segment-wrap {
    display: flex;
    padding-top: 10px;
    opacity: 1;
    transition: transform 0.3s ease;
  }

  .device-control {
    position: relative;
    margin: 0 46px 0 12px;
    transition: transform 0.3s ease;

    .select-all-btn {
      position: absolute;
      right: -22px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background-color: #fff;
      background-clip: padding-box;
      border-radius: 100%;
      border: 1px solid #00000033;
      box-shadow: 0px 5px 6px -2px rgba(0, 0, 0, 0.16);
      z-index: 11;

      &.disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      &:hover {
        background-color: #f4f4f4;
      }
    }

    & /deep/ {
      .ba-button-select {
        border-radius: 7px;

        &.all {
          :not(:last-child) {
            button {
              border-right: 1px solid #ffffff47;
              &:after {
                content: url("../../assets/img/common/icon--chain.svg");
                width: 16px;
                height: 16px;
                position: absolute;
                top: 50%;
                right: -8px;
                transform: translateY(-50%);
              }
            }
          }
        }
      }

      .ba-button {
        button {
          height: 36px;
          margin-left: -1px;
          margin-right: 0px;
          padding: 8px 10px;
          background-color: transparent;

          .icon {
            width: 20px;
            height: 20px;
            left: 10px;
          }

          .text {
            min-width: 41px;
            font-size: 13px;
            line-height: 1;
            margin-left: 25px;
          }

          &.active {
            z-index: 10;
          }
        }

        &:first-child {
          button {
            border-top-left-radius: 7px;
            border-bottom-left-radius: 7px;
            margin-left: 0px;
          }
        }

        &:last-child {
          button {
            border-top-right-radius: 7px;
            border-bottom-right-radius: 7px;

            .icon {
              left: 10px;
            }

            .text {
              padding: 0 5px 0 3px;
            }
          }
        }
      }
    }

    &.all {
      .select-all-btn {
        border-color: #ffffffcc;
        background-color: #3eaa08;
      }

      & /deep/ .ba-button {
        button {
          .icon {
            left: 50%;
            transform: translateX(-50%);
          }
        }
      }
    }
  }

  .source-control {
    display: flex;
    align-items: center;
    gap: 6px;

    .ui_checkbox {
      padding: 0 6px;
      margin: 0;

      & /deep/ {
        .icon {
          left: 6px;
        }
        .text {
          margin-left: 24px;
          font-size: 13px;
        }
      }
    }
  }

  &.mine {
    &.is-ga {
      .control-top {
        .site-list {
          opacity: 0.4;
          pointer-events: none;
        }
      }
      .control-bottom {
        .view-control {
          .report-view {
            &.ranking,
            &.journey,
            &.trend {
              pointer-events: none;
              [class^="icon icon--report"],
              .button-label {
                opacity: 0.4;
              }
            }
          }
        }
      }
    }
  }
}

.calendar-enter-active,
.calendar-leave-active {
  transform-origin: top;
  transition: transform 0.3s ease, opacity 0.3s ease, height 0.3s ease !important;
}

.calendar-enter,
.calendar-leave-to {
  transform: translateY(-30px) scaleY(0.5) !important;
  opacity: 0;
}

.temp-ai-data-button {
  cursor: pointer;
  transition: right 500ms;
  position: fixed;
  bottom: 80px;
  right: 20px;
  .minimized {
    display: none;
  }
}

.docent-wrap {
  flex-shrink: 0;
  position: relative;
  margin: 11px 16px 0 11px;
  width: 85px;
  height: 85px;
  transition: transform 0.3s ease, opacity 0.3s ease;

  .icon--beta {
    position: absolute;
    top: -7.5px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 20;
  }
  .docent {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: 100%;
    height: 100%;
    padding: 10px 9px 0;
    border-radius: 10px;
    background: url("../../assets/img/control/icon--gradient-blue.svg")
        no-repeat center bottom,
      linear-gradient(97deg, #0085ff 3.75%, #0058dd 97.33%);
    background-size: auto, cover;
    box-shadow: 0 18px 16px -12px rgba(0, 0, 0, 0.24);
    box-sizing: border-box;
    transition: transform 0.2s ease;
    cursor: pointer;
    &-label {
      color: #fff;
      text-shadow: 0 8px 12px rgba(0, 0, 0, 0.12);
      font-size: 12px;
      font-weight: 600;
      line-height: 120%;
    }
    &-robot {
      width: 50px;
      height: 50px;
      background-image: url("../../assets/img/control/icon--becle-default.svg");
      background-size: 100% 100%;
      background-repeat: no-repeat;
      background-position: center center;
    }
    &:hover {
      background: linear-gradient(97deg, #4fb7ff 3.75%, #0086ff 97.33%);
      transform: scale(1.1);
      .docent-robot {
        background-image: url("../../assets/img/control/icon--becle-ani.svg?url");
      }
    }
    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }

    &.active {
      &:after {
        content: "";
        position: absolute;
        bottom: 8px;
        right: 8px;
        width: 6px;
        height: 6px;
        background-color: #57ab00;
        border: 1px solid #fff;
        border-radius: 50px;
      }
    }
  }
  .icon--coming-soon-label {
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
